import { Link } from '../../components/Link';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth';
import { api } from '../../lib/api';
import { Icon } from '../../components/icons';
import { PublicFooter } from '../../components/PublicFooter';
import { PublicHeader } from '../../components/PublicHeader';
import { HeroCarousel } from '../../components/HeroCarousel';
import { HowItWorksStory } from '../../components/HowItWorksStory';
import { Button } from '../../components/ui';
import { SITE_ORIGIN, Seo, softwareApplicationSchema, PRODUCT_DESCRIPTION } from '../../lib/seo';
import { useFeatures, type FeatureKind } from '../../lib/useFeatures';
import { useReveal } from '../../lib/useReveal';
import type { CSSProperties } from 'react';
import type { LandingCarouselConfig } from '../../lib/types';

/**
 * One flagship product every plan uses. Bot Anomaly Detection and Email
 * Verification merged into a single always-coupled action — every /v1/emails
 * call runs both layers and charges the combined credit weight. The tile below
 * deep-links into the merged /features/bot-detection marketing page.
 */
const CORE_PRODUCTS: Array<{
  slug: string;
  icon: React.ReactNode;
  title: string;
  tagline: string;
  body: string;
  accent: 'accent' | 'allow';
}> = [
  {
    slug: 'bot-detection',
    icon: <Icon.ShieldAlert />,
    title: 'Bot Anomaly Detection & Email Verification',
    tagline: 'One call per signup — cluster + risk score + mailbox check, always together',
    body: "Every /v1/emails call runs the full stack: near-duplicate clustering + a dozen structural heuristics for anomaly detection, then an authoritative mailbox-level check (deliverable / invalid / disposable / spamtrap). One response carries the risk score, cluster id, action band, reasons, and verification status. Bot waves surface as clusters minutes after they start; dead mailboxes get caught before they bounce; spamtraps get force-declined so they can't tank your sender reputation. No per-request toggles — one action, one credit weight.",
    accent: 'accent',
  },
];

const FEATURES = [
  {
    icon: <Icon.Radar />,
    title: 'Near-duplicate clustering',
    body: 'Bot-enumerated and multi-accounting signups get grouped by fuzzy-matching handles as they arrive — the bigger the cluster, the higher the risk.',
  },
  {
    icon: <Icon.Activity />,
    title: 'Risk scoring → action',
    body: 'Every address is scored on structure + cluster size and resolved to allow, verify, or review. Tune the thresholds per list.',
  },
  {
    icon: <Icon.Layers />,
    title: 'Lists & filters',
    body: 'Scope detection to a specific list so duplicates are only evaluated against the right cohort. Per-list tuning knobs.',
  },
  {
    icon: <Icon.Key />,
    title: 'API-first, multi-tenant',
    body: 'Integrate in minutes with a scoped API key. Strict per-tenant isolation on every layer — your data never mixes.',
  },
  {
    icon: <Icon.Mail />,
    title: 'Built to scale',
    body: 'Bucketed, windowed matching keeps detection fast even with millions of addresses per tenant.',
  },
];

// The static 3-step "How it works" grid was replaced by <HowItWorksStory /> — the scroll-driven
// onboarding walk-through (see components/HowItWorksStory.tsx).

/**
 * Workspace add-ons surfaced below the core feature grid. Each ships as an opt-in slider on
 * the in-app Profile → Add-ons page with monthly Stripe-billed tiers; Pro plans bundle some
 * free per tier. The numbers + tier ladders mirror PHONE_VALIDATION_TIERS / INBOX_PLACEMENT_TIERS
 * / BLACKLIST_TIERS in src/lib/types.ts and ADDON_TIERS on the API.
 */
/**
 * Per-feature marketing tile. `kind` maps to the global flag; `slug` deep-links
 * into the dedicated /features/<slug> marketing page; `accent` themes the tile's
 * icon + link colour so the addon strip reads as a proper rainbow rather than one
 * flat green block.
 */
const ADDONS: Array<{
  kind: FeatureKind;
  slug: string;
  icon: React.ReactNode;
  title: string;
  tagline: string;
  body: string;
  accent: 'allow' | 'verify' | 'review' | 'accent';
}> = [
  {
    kind: 'domain_health',
    slug: 'domain-health',
    icon: <Icon.ShieldCheck />,
    title: 'Domain Health',
    tagline: 'SPF · DKIM · DMARC · MX in one place',
    body: 'In-house DNS checker scores every sender and recipient domain you ingest. Surfaces as a per-record badge so you can flag mismatched / unprotected senders before they cause deliverability damage. Bundled free with Pro plans.',
    accent: 'accent',
  },
  {
    kind: 'inbox_placement',
    slug: 'inbox-placement',
    icon: <Icon.Mail />,
    title: 'Inbox Placement',
    tagline: 'See exactly where your mail lands',
    body: 'Send a test from your production sender to a seed list — we report inbox vs spam vs missing per ESP (Gmail / Yahoo / Outlook). 30/60/90-day reputation trend shows whether your deliverability is improving or regressing. Pairs naturally with Warmup — diagnose with Inbox Placement, fix with Warmup.',
    accent: 'verify',
  },
  {
    kind: 'warmup_inbox',
    slug: 'warmup',
    icon: <Icon.Activity />,
    title: 'Warmup',
    tagline: 'Build sender reputation across every mailbox you sell from',
    body: 'Connect 1, 10 or 50+ sending mailboxes (Gmail / Microsoft / SMTP) and we auto-warm them via partner send/reply loops. Reputation 0-100 per ESP (Google / Microsoft / Other), per-mailbox health-check, optional Pro / Max content tuning. The stepping stone to good Inbox Placement scores — most "we land in spam" problems are mailbox-reputation problems before they\'re domain problems. IP-level warmup is on our near-term roadmap.',
    accent: 'allow',
  },
  {
    kind: 'phone_validation',
    slug: 'phone-validation',
    icon: <Icon.User />,
    title: 'Phone Validation',
    tagline: 'Catch VoIP & toll-free numbers before SMS',
    body: 'Look up any number\'s country, carrier, and line type (mobile / VoIP / toll-free / fixed). Cache-first — repeat lookups of the same number are free. Single + bulk modes.',
    accent: 'allow',
  },
  {
    kind: 'blacklist',
    slug: 'blacklist-checks',
    icon: <Icon.ShieldAlert />,
    title: 'Blacklist Checks',
    tagline: 'Blacklist coverage on every domain',
    body: '50+ industry blacklists checked on every domain you ingest. Hit count + listing names land on the matching Domain Health snapshot so deliverability problems surface before they land in production traffic.',
    accent: 'review',
  },
];

const TILE_ACCENT: Record<'allow' | 'verify' | 'review' | 'accent', { icon: string; border: string; link: string }> = {
  allow: { icon: 'text-allow', border: 'hover:border-allow/40', link: 'text-allow' },
  verify: { icon: 'text-verify', border: 'hover:border-verify/40', link: 'text-verify' },
  review: { icon: 'text-review', border: 'hover:border-review/40', link: 'text-review' },
  accent: { icon: 'text-accent', border: 'hover:border-accentdeep/50', link: 'text-accent' },
};

export function LandingPage() {
  const { feature } = useFeatures();
  // SEO meta + Organization knowledge-graph schema. The Organization schema is
  // what surfaces our brand in Google's right-hand knowledge panel + sitelinks.
  const origin = SITE_ORIGIN;
  // Auth-aware hero CTAs: when signed in, "Start free" becomes the in-app button and
  // "Open console" jumps straight to the right surface (skip the /login bounce).
  const user = useAuth((s) => s.user);
  const consolePath = user?.role === 'super_admin' ? '/admin' : '/app';
  const consoleLabel = user?.role === 'super_admin' ? 'Open admin' : 'Open console';

  // Super-admin-configured hero carousel. Falls back to the static gradient hero when
  // disabled / empty. Public, cached — one cheap call shared across visitors.
  const { data: carousel } = useQuery({
    queryKey: ['landing-carousel'],
    queryFn: () => api<LandingCarouselConfig>('/platform/landing', { auth: false }),
    staleTime: 5 * 60_000,
  });
  const carouselOn = Boolean(carousel?.enabled && carousel.slides.length > 0);

  // Scroll-reveal container — descendants tagged .sg-reveal fade up as they enter view.
  const revealRef = useReveal<HTMLDivElement>();

  return (
    <>
      <Seo
        title="Catch fake & duplicate signups"
        description={PRODUCT_DESCRIPTION}
        path="/"
        keywords="email validation api, email verification, signup fraud, duplicate detection, email deliverability, domain health, inbox placement, mailbox warmup, phone validation, dnsbl checker"
        // Organization + WebSite now come from the site-wide base graph (see Seo/baseGraph), so the
        // homepage only declares the product entity. Its @id is shared with the pricing page, which
        // attaches the real price range to the same SoftwareApplication.
        jsonLd={[
          softwareApplicationSchema({
            name: 'SieveGuard',
            description: PRODUCT_DESCRIPTION,
            url: `${origin}/`,
          }),
        ]}
      />
    <div ref={revealRef} className="relative mx-auto max-w-[1210px] px-6 pb-20">
      <PublicHeader />

      {/* Ambient accent glows behind the hero — fixed, non-interactive, pure decoration. */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[640px] overflow-hidden">
        <div className="sg-float absolute -left-24 top-24 h-72 w-72 rounded-full bg-accent/10 blur-[120px]" />
        <div className="sg-float absolute right-0 top-10 h-80 w-80 rounded-full bg-allow/10 blur-[130px]" style={{ animationDelay: '2s' }} />
        <div className="sg-float absolute left-1/3 top-48 h-64 w-64 rounded-full bg-verify/10 blur-[120px]" style={{ animationDelay: '4s' }} />
      </div>

      {/* <main> landmark: PSI + Lighthouse flag its absence at ~5-point cost to
          accessibility, and it's what screen readers use to jump past the header
          straight to the content. PublicHeader is a <header> and PublicFooter is
          a <footer>, so wrapping the between here keeps a single <main> per page. */}
      <main>
      {/* The hero backdrop is a full-bleed CSS background, so Chrome treats it as the LCP element.
          Preload it (18KB webp, high priority) so LCP lands near the text paint instead of after
          the rest of the page — measured: without this it regressed LCP to ~4.9s. React 19 hoists
          this <link> into <head>; it's rendered only here, so only the homepage pays for it. */}
      <link rel="preload" as="image" href="/heroes/home.webp" fetchPriority="high" />
      <section className="sg-hero-gradient relative mt-6 overflow-hidden rounded-[28px] border border-border px-6 py-20 text-center">
        {/* AI-generated network backdrop, layered over the animated gradient. Two theme-specific
            layers cross-faded by CSS (see .sg-hero-dark/.sg-hero-light in index.css): the dark
            glow-on-black is screen-blended so only the cyan glow shows; the light colour-on-white
            (home-light.webp) is multiply-blended so the white drops out. CSS backgrounds — NOT
            <img> — so neither is an LCP candidate: the H1 stays the LCP and the CWV work holds. */}
        <div
          aria-hidden
          className="sg-hero-dark pointer-events-none absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/heroes/home.webp)' }}
        />
        <div
          aria-hidden
          className="sg-hero-light pointer-events-none absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/heroes/home-light.webp)' }}
        />
        <div className="relative mx-auto max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-panel/80 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted backdrop-blur">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" /> Email integrity, as a service
          </span>
          <h1 className="mt-6 font-display text-5xl font-extrabold leading-[1.05] tracking-[-0.02em] md:text-6xl">
            Catch fake &amp; duplicate signups{' '}
            <span className="bg-gradient-to-r from-accent via-allow to-verify bg-clip-text text-transparent">
              before
            </span>{' '}
            they cost you.
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-muted">
            SieveGuard detects bot-enumerated and multi-accounting registrations by clustering
            near-duplicate email addresses, scoring risk, and verifying deliverability — in real time,
            over a simple API.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            {user ? (
              <Link to={consolePath}>
                <Button variant="primary" className="h-12 px-6">
                  {consoleLabel} <Icon.Play width={15} height={15} />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/register">
                  <Button variant="primary" className="h-12 px-6">
                    Start free <Icon.Play width={15} height={15} />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="ghost" className="h-12 px-6">
                    Sign in
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Super-admin-configured carousel sits inside the hero band when enabled. */}
        {carouselOn && carousel && <HeroCarousel config={carousel} />}
      </section>

      {/* Core products — the two features every plan uses. Same tile shape as ADDONS
          below so both surfaces read consistently, but this section leads because
          detection + verification are the flagship products. */}
      <section className="sg-defer mt-20" style={{ '--sg-defer-h': '719px', '--sg-defer-h-md': '450px' } as CSSProperties}>
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-panel px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" /> Core products
          </span>
          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight">
            One call catches the bot waves and confirms the mailbox
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-[14.5px] leading-relaxed text-muted">
            Bot Anomaly Detection and Email Verification run together as a single action —
            near-duplicate clustering, structural risk scoring, and mailbox-level
            deliverability, all in one <code className="rounded bg-panel2 px-1 py-0.5 text-[12.5px]">/v1/emails</code> response.
            No per-request toggles: one credit weight per screening covers everything.
          </p>
        </div>
        <div className="mx-auto mt-10 grid max-w-2xl gap-4">
          {CORE_PRODUCTS.map((p, i) => {
            const t = TILE_ACCENT[p.accent];
            return (
              <Link
                key={p.title}
                to={`/features/${p.slug}`}
                style={{ transitionDelay: `${i * 60}ms` }}
                className={`sg-reveal group rounded-2xl border border-bordersoft bg-panel p-6 transition hover:-translate-y-1 ${t.border}`}
              >
                <div className="flex items-baseline gap-3">
                  <span className={t.icon}>{p.icon}</span>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold">{p.title}</h3>
                    <div className="font-mono text-[11px] text-faint">{p.tagline}</div>
                  </div>
                </div>
                <p className="mt-3 text-[13.5px] leading-relaxed text-muted">{p.body}</p>
                <div className={`mt-3 font-mono text-[11px] transition group-hover:underline ${t.link}`}>
                  Read more →
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="sg-defer mt-16 grid gap-4 md:grid-cols-3" style={{ '--sg-defer-h': '931px', '--sg-defer-h-md': '350px' } as CSSProperties}>
        {FEATURES.map((f, i) => (
          <div
            key={f.title}
            className="sg-reveal rounded-2xl border border-bordersoft bg-panel p-6 transition hover:-translate-y-1 hover:border-accentdeep/50 hover:shadow-[0_18px_50px_rgba(62,200,255,0.08)]"
            style={{ transitionDelay: `${i * 60}ms` }}
          >
            <span className="text-accent">{f.icon}</span>
            <h3 className="mt-3 text-base font-semibold">{f.title}</h3>
            <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{f.body}</p>
          </div>
        ))}
      </section>

      <section className="sg-defer mt-20" style={{ '--sg-defer-h': '1699px', '--sg-defer-h-md': '918px' } as CSSProperties}>
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-panel px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
            <span className="h-1.5 w-1.5 rounded-full bg-allow" /> Add-ons
          </span>
          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight">
            Layer on deliverability + signal verification
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-[14.5px] leading-relaxed text-muted">
            Four optional workspace features that plug directly into your detection flow. Pro
            plans bundle some free; everything stacks on a Stripe-billed slider with pro-rated
            upgrades and downgrades.
          </p>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {ADDONS.filter((a) => feature(a.kind)).map((a, i) => {
            const t = TILE_ACCENT[a.accent];
            return (
              <Link
                key={a.title}
                to={`/features/${a.slug}`}
                style={{ transitionDelay: `${i * 60}ms` }}
                className={`sg-reveal group rounded-2xl border border-bordersoft bg-panel p-6 transition hover:-translate-y-1 ${t.border}`}
              >
                <div className="flex items-baseline gap-3">
                  <span className={t.icon}>{a.icon}</span>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold">{a.title}</h3>
                    <div className="font-mono text-[11px] text-faint">{a.tagline}</div>
                  </div>
                </div>
                <p className="mt-3 text-[13.5px] leading-relaxed text-muted">{a.body}</p>
                <div className={`mt-3 font-mono text-[11px] transition group-hover:underline ${t.link}`}>
                  Read more →
                </div>
              </Link>
            );
          })}
        </div>
        <div className="mt-6 flex flex-wrap justify-center gap-4 text-[13.5px]">
          <Link to="/features" className="text-accent hover:underline">
            Browse all features →
          </Link>
          <Link to="/pricing" className="text-accent hover:underline">
            See add-on tiers + which Pro plan bundles each →
          </Link>
        </div>
      </section>

      <section className="sg-defer mt-20 rounded-2xl border border-bordersoft bg-panel p-10" style={{ '--sg-defer-h': '552px', '--sg-defer-h-md': '249px' } as CSSProperties}>
        <div className="grid items-center gap-6 md:grid-cols-[1.2fr_1fr]">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-allow/30 bg-allow/5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-allow">
              <span className="h-1.5 w-1.5 rounded-full bg-allow" /> Free tools · No signup
            </span>
            <h2 className="mt-5 font-display text-2xl font-bold tracking-tight md:text-3xl">
              Try the engines we use, free.
            </h2>
            <p className="mt-3 max-w-md text-[14px] leading-relaxed text-muted">
              SPF / DKIM / DMARC checkers and a content spam-scorer. Same engines that
              power the in-app Domain Health and Inbox Placement add-ons — exposed
              without an account so you can sanity-check a domain in seconds.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Link to="/tools/spf-checker" className="rounded-xl border border-bordersoft bg-panel2 p-3 text-[13px] font-medium transition hover:border-accentdeep hover:text-accent">
              SPF checker →
            </Link>
            <Link to="/tools/dkim-checker" className="rounded-xl border border-bordersoft bg-panel2 p-3 text-[13px] font-medium transition hover:border-accentdeep hover:text-accent">
              DKIM checker →
            </Link>
            <Link to="/tools/dmarc-checker" className="rounded-xl border border-bordersoft bg-panel2 p-3 text-[13px] font-medium transition hover:border-accentdeep hover:text-accent">
              DMARC checker →
            </Link>
            <Link to="/tools/email-spam-checker" className="rounded-xl border border-bordersoft bg-panel2 p-3 text-[13px] font-medium transition hover:border-accentdeep hover:text-accent">
              Email spam checker →
            </Link>
          </div>
        </div>
      </section>

      <HowItWorksStory />

      {feature('agency') && (
      <section className="sg-defer mt-20 rounded-2xl border border-accentdeep/30 bg-accentdeep/5 p-10" style={{ '--sg-defer-h': '715px', '--sg-defer-h-md': '328px' } as CSSProperties}>
        <div className="grid items-center gap-6 md:grid-cols-[1.3fr_1fr]">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-accentdeep/40 bg-accentdeep/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-accent">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" /> Built for agencies
            </span>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight md:text-3xl">
              Roll up email integrity across every client.
            </h2>
            <p className="mt-3 max-w-md text-[14px] leading-relaxed text-muted">
              One login, every client workspace. Volume discounts are agreed 1-on-1 — we
              set a tier ladder for your agency over a short call and demo, then it
              auto-applies to every client subscription and grows with your roster.
            </p>
            <Link to="/agencies" className="mt-5 inline-block">
              <Button variant="primary" className="h-11 px-5">
                Explore the agency tier →
              </Button>
            </Link>
          </div>
          <div className="rounded-xl border border-bordersoft bg-panel p-5">
            <div className="font-mono text-[10.5px] uppercase tracking-wider text-faint">How agency pricing works</div>
            <ol className="mt-3 grid gap-3 text-[13px]">
              {[
                ['Talk to us', 'A short call and demo to understand your roster and growth plans.'],
                ['We agree your ladder', 'A discount ladder set 1-on-1 for your agency — never a published one-size-fits-all.'],
                ['Portal unlocks', 'Discounts auto-apply to every client subscription and grow as you do.'],
              ].map(([title, desc], i) => (
                <li key={title} className="flex gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accentdeep/15 font-display text-[11px] font-bold text-accent">
                    {i + 1}
                  </span>
                  <span>
                    <span className="font-display font-semibold text-txt">{title}</span>
                    <span className="mt-0.5 block leading-relaxed text-muted">{desc}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>
      )}

      {feature('affiliate') && (
      <section className="sg-defer mt-20 rounded-2xl border border-allow/30 bg-allow/5 p-10" style={{ '--sg-defer-h': '726px', '--sg-defer-h-md': '345px' } as CSSProperties}>
        <div className="grid items-center gap-6 md:grid-cols-[1.3fr_1fr]">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-allow/40 bg-allow/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-allow">
              <span className="h-1.5 w-1.5 rounded-full bg-allow" /> Partner with us
            </span>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight md:text-3xl">
              Earn recurring commission on every client you refer.
            </h2>
            <p className="mt-3 max-w-md text-[14px] leading-relaxed text-muted">
              Join the affiliate program and earn commission on every business you send our
              way — on both monthly and yearly plans, for a full year. Transparent tracking,
              no caps on how many clients you bring.
            </p>
            <Link to="/partners" className="mt-5 inline-block">
              <Button variant="primary" className="h-11 px-5">
                Become a partner →
              </Button>
            </Link>
          </div>
          <div className="rounded-xl border border-bordersoft bg-panel p-5">
            <div className="font-mono text-[10.5px] uppercase tracking-wider text-faint">How it works</div>
            <ol className="mt-3 grid gap-3 text-[13px]">
              {[
                ['Apply', 'A quick application — we review and approve partner accounts.'],
                ['Share your link', 'Refer businesses with your unique tracking link.'],
                ['Get paid', 'Earn recurring commission on every referred subscription for a year.'],
              ].map(([title, desc], i) => (
                <li key={title} className="flex gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-allow/15 font-display text-[11px] font-bold text-allow">
                    {i + 1}
                  </span>
                  <span>
                    <span className="font-display font-semibold text-txt">{title}</span>
                    <span className="mt-0.5 block leading-relaxed text-muted">{desc}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>
      )}

      <section className="sg-defer mt-20 rounded-2xl border border-bordersoft bg-gradient-to-b from-panel to-panel2 p-10 text-center" style={{ '--sg-defer-h': '278px', '--sg-defer-h-md': '222px' } as CSSProperties}>
        <h2 className="font-display text-3xl font-bold tracking-tight">
          Screen your first signups in minutes.
        </h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-muted">
          Create a workspace, generate an API key, and start sending addresses.
        </p>
        <Link to="/register" className="mt-6 inline-block">
          <Button variant="primary" className="h-12 px-6">
            Create your workspace
          </Button>
        </Link>
      </section>
      </main>

      <PublicFooter />
    </div>
    </>
  );
}

// Default export required by React Router framework-mode route modules.
export default LandingPage;
